// Random Event System for Canntycoon

let eventIdCounter = 1;

// ── Equipment Issue Types ──────────────────────────────────────────────────────
const EQUIPMENT_ISSUES = [
  { name: 'Belt Failure',     requiresParts: true,  partsCost: 50  },
  { name: 'Refrigerant Leak', requiresParts: true,  partsCost: 120 },
  { name: 'Filter Clog',      requiresParts: false, partsCost: 0   },
  { name: 'Ballast Blown',    requiresParts: true,  partsCost: 80  },
  { name: 'Pump Failure',     requiresParts: true,  partsCost: 60  },
  { name: 'Sensor Fault',     requiresParts: false, partsCost: 0   },
];

// ── Event Definitions ─────────────────────────────────────────────────────────
const EVENT_TYPES = {
  pest_outbreak: {
    label: 'Pest Outbreak',
    icon: '🐛',
    description: (room) => `Pests detected in ${room.name}! Treat immediately or lose the batch.`,
    severity: 'high',
    resolution: 'Spend $150 on pesticide to eliminate.',
    resolutionCost: 150,
    damagePerTick: 0.10, // ~17 health/week if untreated — serious but survivable
  },
  powdery_mildew: {
    label: 'Powdery Mildew',
    icon: '🍄',
    description: (room) => `Powdery mildew spreading in ${room.name}. Humidity too high?`,
    severity: 'high',
    resolution: 'Spray fungicide ($200). Lower humidity.',
    resolutionCost: 200,
    damagePerTick: 0.07, // ~12 health/week — quality hit, not batch-ending
  },
  equipment_failure: {
    label: 'Equipment Failure',
    icon: '⚙️',
    description: (room, equip) => `${equip} in ${room.name}. Environment unstable.`,
    severity: 'medium',
    resolution: 'Repair equipment. Assign a maintenance worker to reduce cost.',
    resolutionCost: 0, // computed dynamically at resolve time based on issue + maintenance worker
    damagePerTick: 0.03, // minor plant stress ~5 health/week
    workerMoralePenalty: 0.5, // morale/tick for workers assigned to the affected room
  },
  regulatory_inspection: {
    label: 'Regulatory Inspection',
    icon: '📋',
    description: () => 'Inspector on site. All records and licenses will be reviewed.',
    severity: 'critical',
    resolution: 'Comply. Have your licenses and records ready.',
    resolutionCost: 0,
    damagePerTick: 0,
  },
  theft: {
    label: 'Theft',
    icon: '🚨',
    description: (room, name) => `${name} was caught stealing from the facility.`,
    severity: 'high',
    resolution: 'Review security footage. Consider improving security.',
    resolutionCost: 0,
    damagePerTick: 0,
  },
  heat_wave: {
    label: 'Heat Wave',
    icon: '🌡️',
    description: (room) => `Extreme heat affecting ${room.name}. HVAC struggling.`,
    severity: 'medium',
    resolution: 'Increase cooling or reduce light intensity temporarily.',
    resolutionCost: 0,
    damagePerTick: 0,
  },
  power_outage: {
    label: 'Power Outage',
    icon: '⚡',
    description: (room) => `Power outage in ${room.name}. All systems offline.`,
    severity: 'critical',
    resolution: 'Get backup power or wait for power to restore.',
    resolutionCost: 250, // generator rental
    damagePerTick: 0.15, // ~25 health/week if unresolved — painful but batch survives
  },
  scammer: {
    label: 'Scam Attempt',
    icon: '⚠️',
    description: () => 'A fraudulent buyer has approached you for a large cash deal.',
    severity: 'medium',
    resolution: 'Decline or risk losing product with no payment.',
    resolutionCost: 0,
    damagePerTick: 0,
  },
  room_inspection: {
    label: 'Room Inspection',
    icon: '🔍',
    description: (room) => `Health & safety inspectors are reviewing ${room.name}. Click to conduct the inspection.`,
    severity: 'warning',
    resolution: 'Conduct inspection. Pass if compliance is good.',
    resolutionCost: 0,
    damagePerTick: 0,
  },
};

// ── Event Probability Calculation ─────────────────────────────────────────────
// Rates are expressed as weekly rates, divided by 168 to get per-tick values.
// rollRandomEvents checks every 6 ticks and multiplies by 6 to correct for sampling frequency.
// Target rates per year: pest ~2×, mildew ~1×, inspection ~3× (unlicensed), outage ~0.5×
export function getEventProbabilities(room, state) {
  if (!room.batch) return {};

  const batch = room.batch;
  const hasPestDetection = state.unlockedTech.includes('B1');
  const hasIPM           = state.unlockedTech.includes('B2');
  const hasUV            = state.unlockedTech.includes('B3');
  const strain           = require('../data/strains').getStrain(batch.strainId);

  // Base weekly rates → per-tick rate (÷168 ticks per week)
  // rollRandomEvents multiplies by 6 to compensate for checking every 6 ticks
  return {
    // ~2/year per room for bagseed-og (susceptibility=0.20)
    pest_outbreak: (0.20 * (strain?.pestSusceptibility || 0.2) * (hasIPM ? 0.6 : 1) * (hasPestDetection ? 0.5 : 1)) / 168,
    // ~1/year; removed moldRisk multiplier (it starts at 0 and never updates, making mildew impossible)
    powdery_mildew: (0.10 * (strain?.moldSusceptibility || 0.2) * (hasUV ? 0.4 : 1)) / 168,
    // ~2/year when failing, ~0.25/year when healthy
    equipment_failure: (room.equipment.hvac.health < 50 ? 0.04 : 0.005) / 168,
    // ~3/year unlicensed; 1× illegal sales warning; ~1/year licensed
    regulatory_inspection: (
      (state.licenseLevel === 0 ? 0.06 : 0.02)
      * (state.illegalSalesCount > 0 ? Math.min(2, 1 + state.illegalSalesCount * 0.15) : 1)
    ) / 168,
    heat_wave: (room.type === 'greenhouse' ? 0.02 : 0.005) / 168,
    // ~0.5/year — rare but impactful ($250 + plant damage)
    power_outage: 0.01 / 168,
    scammer: (state.stage >= 2 ? 0.02 : 0.005) / 168,
    // ~1.5/year per room with an active batch; more likely when compliance is low
    room_inspection: (0.03 * (state.complianceScore < 60 ? 1.5 : 1.0)) / 168,
  };
}

// ── Roll Events for All Rooms ─────────────────────────────────────────────────
export function rollRandomEvents(state) {
  const newEvents = [];
  const newNotifications = [];

  for (const room of state.rooms) {
    if (!room.unlocked) continue;
    const probs = getEventProbabilities(room, state);
    for (const [eventType, prob] of Object.entries(probs)) {
      // Multiply by 6 because rollRandomEvents is only called every 6 ticks,
      // but probabilities are expressed as per-tick rates.
      if (Math.random() < prob * 6) {
        // Don't stack the same event type on same room
        const alreadyActive = state.activeEvents.some(
          e => e.type === eventType && e.roomId === room.id && !e.resolved
        );
        if (alreadyActive) continue;

        const eventDef = EVENT_TYPES[eventType];
        if (!eventDef) continue;

        // For equipment failures, pick a random issue type and use it in the description
        const issue = eventType === 'equipment_failure'
          ? EQUIPMENT_ISSUES[Math.floor(Math.random() * EQUIPMENT_ISSUES.length)]
          : null;

        const event = {
          id: `event-${eventIdCounter++}`,
          type: eventType,
          roomId: room.id,
          roomName: room.name,
          label: eventDef.label,
          icon: eventDef.icon,
          description: issue
            ? eventDef.description(room, issue.name)
            : eventDef.description(room, ''),
          severity: eventDef.severity,
          resolution: eventDef.resolution,
          resolutionCost: eventDef.resolutionCost,
          damagePerTick: eventDef.damagePerTick,
          ...(issue && { issue }),
          startDay: state.gameDay,
          resolved: false,
          resolvedDay: null,
        };

        newEvents.push(event);
        newNotifications.push({
          id: `notif-${Date.now()}-${Math.random()}`,
          message: `${eventDef.icon} ${eventDef.label} in ${room.name}!`,
          type: eventDef.severity,
          day: state.gameDay,
          read: false,
        });
      }
    }
  }

  // Lab events
  const labRoomEvt = state.rooms?.find(r => r.id === 'room-lab');
  if (labRoomEvt?.unlocked && Object.values(labRoomEvt.equipment || {}).some(e => e?.installed)) {
    if (Math.random() < 0.04) {
      const alreadyLabFail = state.activeEvents.some(
        e => e.type === 'lab_equipment_failure' && !e.resolved
      );
      if (!alreadyLabFail) {
        newEvents.push({
          id: `lab-fail-${Date.now()}`, type: 'lab_equipment_failure',
          label: 'Lab Equipment Failure', icon: '⚠️', severity: 'medium',
          description: 'A piece of lab equipment has malfunctioned. RP generation halved until repaired.',
          resolutionCost: 500, resolved: false, roomId: 'room-lab',
          deadline: state.gameDay + 14,
          startDay: state.gameDay,
        });
        newNotifications.push({
          id: `notif-lab-fail-${Date.now()}-${Math.random()}`,
          message: '⚠️ Lab Equipment Failure in Research Lab!',
          type: 'medium',
          day: state.gameDay,
          read: false,
        });
      }
    }
  }
  if (labRoomEvt?.unlocked && state.activeResearch && Math.random() < 0.025) {
    const alreadyContam = state.activeEvents.some(
      e => e.type === 'lab_contamination' && !e.resolved
    );
    if (!alreadyContam) {
      newEvents.push({
        id: `lab-contam-${Date.now()}`, type: 'lab_contamination',
        label: 'Research Contamination', icon: '🧫', severity: 'medium',
        description: 'A contamination incident set back research by 1 week of RP.',
        resolutionCost: 0, resolved: false, roomId: 'room-lab',
        deadline: state.gameDay + 7,
        startDay: state.gameDay,
      });
      newNotifications.push({
        id: `notif-lab-contam-${Date.now()}-${Math.random()}`,
        message: '🧫 Research Contamination in Research Lab!',
        type: 'medium',
        day: state.gameDay,
        read: false,
      });
    }
  }

  // Worker theft check
  for (const worker of state.workers) {
    const { checkTheftRisk } = require('./labor');
    const theft = checkTheftRisk(worker, 0); // TODO: pass actual security level
    if (theft) {
      newNotifications.push({
        id: `notif-theft-${worker.id}`,
        message: `🚨 ${worker.name} was caught stealing $${theft.cashLost}!`,
        type: 'critical',
        day: state.gameDay,
        read: false,
      });
      newEvents.push({
        id: `event-theft-${Date.now()}`,
        type: 'theft',
        workerId: worker.id,
        cashLost: theft.cashLost,
        label: 'Theft',
        icon: '🚨',
        description: `${worker.name} stole from the facility.`,
        severity: 'high',
        resolved: false,
        startDay: state.gameDay,
      });
    }
  }

  return { newEvents, newNotifications };
}

// ── Apply Active Event Damage per Tick ────────────────────────────────────────
export function applyEventDamage(rooms, activeEvents) {
  return rooms.map(room => {
    const roomEvents = activeEvents.filter(e => e.roomId === room.id && !e.resolved && e.damagePerTick > 0);
    if (roomEvents.length === 0 || !room.batch) return room;

    let newBatch = { ...room.batch };
    for (const event of roomEvents) {
      newBatch.health = Math.max(0, newBatch.health - event.damagePerTick);
    }
    return { ...room, batch: newBatch };
  });
}

// ── Room Inspection Outcome ───────────────────────────────────────────────────
// Pass if compliance is decent and no active power outage in the room.
export function resolveRoomInspection(room, state) {
  const hasPowerOutage = (state.activeEvents || []).some(
    e => e.type === 'power_outage' && e.roomId === room?.id && !e.resolved
  );
  const passed = (state.complianceScore >= 60) && !hasPowerOutage;
  return {
    passed,
    closeDays: 5,
    fine: passed ? 0 : 500,
  };
}

// ── Regulatory Inspection Outcome ─────────────────────────────────────────────
export function resolveInspection(state) {
  const { licenseLevel, complianceScore, illegalSalesCount, unlockedTech } = state;
  const hasComplianceSoftware = unlockedTech.includes('D1');

  // Build violations list
  const violations = [];

  if (licenseLevel === 0) {
    violations.push({ type: 'critical', reason: 'Operating without a license' });
  }

  if (illegalSalesCount > 0) {
    violations.push({ type: 'critical', reason: 'Evidence of unlicensed sales detected' });
  }

  if (complianceScore < 60) {
    violations.push({ type: 'moderate', reason: 'Poor compliance record' });
  }

  // Rooms with no batch tracking
  const untrackedRooms = state.rooms.filter(r => r.batch && !r.batch.id);
  if (untrackedRooms.length > 0) {
    violations.push({ type: 'minor', reason: 'Incomplete inventory records' });
  }

  // Software reduces fine risk
  const fineReductionMult = hasComplianceSoftware ? 0.70 : 1.0;
  const totalFines = violations.reduce((sum, v) => {
    const fineSizes = { minor: 500, moderate: 2000, major: 8000, critical: 20000 };
    return sum + (fineSizes[v.type] || 1000) * fineReductionMult;
  }, 0);

  const isShutdown = violations.some(v => v.type === 'critical');
  const repLoss = violations.reduce((sum, v) => {
    const repLosses = { minor: 2, moderate: 8, major: 20, critical: 40 };
    return sum + (repLosses[v.type] || 5);
  }, 0);

  return { violations, totalFines, isShutdown, repLoss };
}
